import { ButtonHTMLAttributes, forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '@utils/tailwind.utils'

export const buttonVariants = cva(
  'font-funnel inline-flex items-center justify-center rounded-[12px] font-medium transition-colors focus:outline-none whitespace-nowrap disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-cw-strong-grey text-white hover:bg-cw-strong-grey/[.6] disabled:bg-cw-lighter-grey-stroke disabled:text-cw-lighter-grey-stroke data-[state=open]:bg-white data-[state=open]:text-cw-midnight-purple',
        ghost:
          'font-funnel inline-flex items-center justify-center rounded-[12px] font-medium transition-colors focus:outline-none whitespace-nowrap disabled:pointer-events-none hover:!bg-cw-purple-light-hov disabled:!text-cw-light-grey-disable data-[state=open]:bg-cw-yellow-hov/[.12] h-12 px-7 py-3.5 text-body1 !text-cw-midnight-purple bg-cw-purple-light',
        primary:
          '!text-white bg-cw-electric-purple hover:bg-cw-electric-purple/80 disabled:bg-cw-electric-purple/40 disabled:!text-white/60',
        yellow:
          'bg-cw-yellow text-cw-midnight-purple hover:bg-cw-yellow-hov !text-cw-midnight-purple disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey data-[state=open]:bg-cw-yellow-hov',
        secondaryYellow:
          '!text-cw-yellow border-2 border-cw-yellow hover:bg-cw-yellow-hov/[.12] disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-yellow-hov/[.12]',
        secondaryWhite:
          '!text-white border-2 border-white hover:bg-white/[.12] disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-white/[.12]',
        secondaryWhiteLight:
          '!text-white border-2 border-cw-purple-light/20 hover:bg-white/[.12] disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-white/[.12]',
        secondaryPurpleLight:
          '!text-cw-electric-purple border-2 border-cw-electric-purple hover:bg-cw-electric-purple/10 disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-electric-purple/10',
        tertiary:
          '!text-cw-yellow hover:text-yellow-hov disabled:!text-cw-light-grey-disable disabled:border-cw-light-grey-disable',
        grey: '!text-cw-light-grey border-2 border-cw-lighter-grey-stroke hover:bg-cw-lighter-grey-stroke/[.18] disabled:text-cw-light-grey disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-lighter-grey-stroke/[.1]',
        black:
          'border-2 border-black hover:bg-black/[.1] disabled:text-cw-black-disabled disabled:border-cw-black-disabled disabled:opacity-100 data-[state=open]:bg-black/[.1]',
        error:
          'bg-cw-system-red hover:bg-cw-system-red/[.8] !text-white disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey data-[state=open]:bg-cw-system-red',
        'grey-fulfilled': '!text-cw-yellow rounded-6md bg-cw-black',
        'strong-grey-fulfilled': '!text-cw-yellow rounded-6md bg-cw-strong-grey',
        text: '!text-cw-yellow hover:text-yellow-hov disabled:text-cw-light-grey !py-2 !px-0 !h-auto',
        textPurple:
          '!text-cw-midnight-purple hover:text-cw-midnight-purple/[.3] disabled:text-cw-light-grey !py-2 !px-0 !h-auto',
        textPurpleBright:
          '!text-cw-electric-purple hover:text-cw-midnight-purple/[.3] disabled:text-cw-light-grey !py-2 !px-0 !h-auto',
        quaternary:
          'text-cw-midnight-purple hover:text-cw-midnight-purple/60 !text-body1',
        ['grey-light']:
          '!text-cw-midnight-purple bg-cw-lighter-grey-stroke hover:bg-cw-light-grey-stroke/[.1] disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey data-[state=open]:bg-cw-lighter-grey-stroke/[.1]',
        ['grey-dark']:
          '!text-cw-midnight-purple bg-cw-grey-light hover:bg-cw-grey-light/80 disabled:bg-cw-grey-light/40 disabled:!text-cw-midnight-purple/40',
        tealBlue:
          '!text-white bg-cw-blue hover:bg-cw-blue/[.6] disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey',
        green:
          '!text-cw-midnight-purple bg-cw-eucal-green disabled:bg-cw-lighter-grey-stroke/10 disabled:text-cw-lighter-grey-stroke/40 hover:bg-cw-eucal-green/80 hover:!text-cw-midnight-purple ',
        purple:
          '!text-white bg-cw-electric-purple hover:bg-cw-electric-purple/80 disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey',
        purpleDark:
          '!text-cw-white bg-cw-midnight-purple hover:bg-cw-electric-purple disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey',
        secondary:
          '!text-cw-midnight-purple border-2 !border-cw-midnight-purple/20 hover:!bg-cw-purple-light-hov disabled:!text-cw-light-grey-disable disabled:!border-cw-midnight-purple/10 data-[state=open]:bg-cw-yellow-hov/[.12]',
        secondaryDark:
          '!text-cw-midnight-purple border-2 !border-cw-midnight-purple/20 hover:!bg-cw-purple-light-hov disabled:!text-cw-light-grey-disable disabled:!border-cw-midnight-purple/10 data-[state=open]:bg-cw-yellow-hov/[.12]',
        secondaryLight:
          '!text-cw-midnight-purple border-2 !border-cw-midnight-purple/20 hover:!bg-cw-purple-light-hov disabled:!text-cw-light-grey-disable disabled:!border-cw-midnight-purple/10 data-[state=open]:bg-cw-purple-light-hov',
        brightPurple:
          '!text-cw-electric-purple border-2 border-cw-electric-purple hover:bg-cw-electric-purple/10 disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-electric-purple/10',
        secondaryGreen:
          '!text-cw-eucal-green border-2 border-cw-eucal-green hover:bg-cw-eucal-green-hov/[.12] disabled:!text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-yellow-hov/[.12]',
      },
      size: {
        xxs: 'px-2 py-1 text-xs',
        xs: 'px-3 py-2 text-body2',
        sm: 'h-10 px-5 py-3 ',
        md: 'h-12 px-7 py-3.5 text-body1',
        lg: 'h-14 px-8 py-4 text-body1',
        xl: 'h-16 px-9 py-4.5 text-h5',
        'sm-responsive': 'px-4 py-2 xs:px-5 xs:py-3',
      },
      shape: {
        default: '',
        pill: '!rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      shape: 'default',
    },
  },
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
Button.displayName = 'Button'
